import { ContactType } from '@commerce-profile-webcomponents/sdk';
import type { Contact } from '@commerce-profile-webcomponents/sdk';
import { ConsoleLogger, DateFormats, DateOrTime, formatPrice } from '@nextgen-web-framework/core';
import type { BundlePropType } from '@commerce-product-webcomponents/ui';
import { Products, ProductData, Slot } from '@commerce-product-webcomponents/sdk';
import type { CartEntryDetails, BundleSlot, SlotEntry } from '@commerce-cart-webcomponents/sdk';
import { Address, Entity, OrderLine } from '../services';
import { BundleTypesEnums, ICancelCodeCMS, OrderDeliveryTypeEnum, PostpurchaseConstantData } from '../types';
import {
  IAddress,
  IOrderDetails,
  IProductWithImageInterface,
  ISubscriptionProduct,
  SubscriptionProductType,
  CreditNoteDocumentList,
} from '../types/postpurchaseTypes';
import {
  getOrderCancellationDetails,
  getOrderDeliveryType,
  isOrderCancelled,
  productBasicData,
  checkCancellationCreditNote,
  dateTimeFormatter,
  checkDerivedAvailablityStatus,
} from './commonUtils';

export function getAddressObj(
  addresses: Address[],
  addressReference: string,
  addressType = '',
  deliveryInstructions: string,
  storeId: string,
  orderEmail?: string,
) {
  let addressObj: IAddress & { pickUpPhone?: string } = {};
  if (addressReference && addresses?.length) {
    const address = addresses.find((addr) => addr?.id === addressReference);
    if (address) {
      addressObj =
        addressType === OrderDeliveryTypeEnum.HOMEDELIVERY
          ? {
              deliveryAddress: getShippingAddress(address, deliveryInstructions),
              customerName: address?.careOfName,
              email: orderEmail,
              phoneNo: address?.telephoneNumber,
            }
          : {
              storeName: address?.careOfName,
              storePhone: address?.telephoneNumber,
              storeAddress: getAddressString(address),
              latitude: address?.geoLocation?.latitude,
              longitude: address?.geoLocation?.longitude,
              storeId: storeId,
            };
    }
  }
  return addressObj;
}

function getShippingAddress(address: Address, deliveryInstructions: string): Contact {
  return {
    contactId: address.id || '',
    contactType: ContactType.Shipping,
    addrLineOne: address?.line1,
    addrLineTwo: address?.line2,
    addrLineThree: address?.line3,
    state: address?.state,
    subDistrict: address?.subDistrict,
    district: address?.district,
    postalCode: address?.postalCode,
    deliveryInstructions: deliveryInstructions?.trim(),
  };
}

export function getShippingAddressString(address: Contact | undefined) {
  let addressStr = '';
  addressStr += address?.addrLineOne ? `${address?.addrLineOne}, ` : '';
  addressStr += address?.addrLineTwo ? ` ${address?.addrLineTwo}, ` : '';
  addressStr += address?.addrLineThree ? ` ${address?.addrLineThree}, ` : '';
  addressStr += address?.state ? `${address?.state}, ` : '';
  addressStr += address?.countryCode ? `${address?.countryCode}, ` : '';
  addressStr += address?.postalCode ? `${address?.postalCode} ` : '';
  addressStr += address?.deliveryInstructions ? `(${address?.deliveryInstructions})` : '';
  return addressStr;
}

export function getAddressString(address: Address) {
  let addressStr = '';
  addressStr += address?.line1 ? `${address?.line1}` : '';
  addressStr += address?.line2 ? ` ${address?.line2}` : '';
  addressStr += address?.line3 ? ` ${address?.line3}` : '';
  addressStr += address?.line4 ? ` ${address?.line4}` : '';
  addressStr += addressStr !== '' ? ', ' : '';
  addressStr += address?.postalCode ? `${address?.postalCode}, ` : '';
  addressStr += address?.state ? `${address?.state}, ` : '';
  addressStr += address?.isoCountryCode ? `${address?.isoCountryCode} ` : '';

  return addressStr;
}

export function checkChildOrder(loggedInAccountId: string, order: Entity) {
  return order?.orderForSalesAccount?.accountId !== order?.orderingSalesAccount?.accountId &&
    order?.orderForSalesAccount?.accountId === loggedInAccountId
    ? true
    : false;
}

export function checkParentOrder(loggedInAccountId: string, order: Entity) {
  //used for proxy order
  return order?.orderForSalesAccount?.accountId !== order?.orderingSalesAccount?.accountId &&
    order?.orderingSalesAccount?.accountId === loggedInAccountId
    ? true
    : false;
}

export function checkIsParentOfGroupOrder(loggedInAccountId: string, order: Entity) {
  //only for group orders to check parent
  return order?.orderingSalesAccount?.accountId === loggedInAccountId && order.groupOrderId ? true : false;
}

export function checkIsOwnOrder(loggedInAccountId: string, order: Entity) {
  // for normal and proxy parent order
  return !order?.groupOrderId && order?.orderingSalesAccount?.accountId === loggedInAccountId ? true : false;
}

export function checkIsProxyOrder(order: Entity) {
  // proxy child order
  return !order?.groupOrderId && order?.orderForSalesAccount?.accountId !== order?.orderingSalesAccount?.accountId;
}
export function checkShippingFee(
  isGroupOrder?: boolean,
  deliveryFee?: number,
  groupDeliveryFee?: number,
  isoCurrencyCode?: string,
) {
  const fee = isGroupOrder ? groupDeliveryFee : deliveryFee;
  if (typeof fee === 'undefined') {
    return undefined;
  }

  return Number(fee) !== 0 ? `${formatPrice(fee, isoCurrencyCode)}` : PostpurchaseConstantData.FREE;
}

function createSOPBundle(sopOrderline?: OrderLine, creditNoteDocumentUrlList?: Array<CreditNoteDocumentList>) {
  let hasBundleSOPCancelledItem = false;
  const sopBundleData: BundlePropType[] = [];
  sopOrderline?.bundleProducts?.forEach((bundle) => {
    checkCancellationCreditNote(bundle, creditNoteDocumentUrlList);
    const data = productBasicData(bundle);
    sopBundleData.push({
      ...data,
      servingSize: bundle?.itemSize || '',
      quantity: bundle?.orderedQuantity || 0,
      derivedAvailablityStatus: checkDerivedAvailablityStatus(bundle),
      firstAvailableFutureDate: bundle?.estimatedShipDate,
    } as BundlePropType);
    if (bundle?.cancelledQuantity && bundle?.cancelledQuantity > 0 && !hasBundleSOPCancelledItem) {
      hasBundleSOPCancelledItem = true;
    }
  });
  return { sopBundleData, hasBundleSOPCancelledItem };
}

function calculateBvValue(sopOrderline: OrderLine) {
  const configs = ConfigService.getConfigs();
  const hideBusinessVolume = configs.NEXT_HIDE_BV as boolean;

  if (hideBusinessVolume) {
    return undefined;
  } else {
    return sopOrderline?.bundleProducts?.length
      ? sopOrderline?.calculatedBundleOriginalLineBVAmount?.toString()
      : sopOrderline?.calculatedSubscriptionOriginalLinePVAmount?.toString() || '';
  }
}

export function createSOPProductData(
  sopProduct: OrderLine,
  currencyCode?: string,
  isReplacementOrder?: boolean,
  creditNoteDocumentUrlList?: Array<CreditNoteDocumentList>,
) {
  let hasSOPCancelledItems = false;
  const data: SubscriptionProductType[] = [];
  sopProduct.subscriptionProducts?.forEach((sopOrderline) => {
    checkCancellationCreditNote(sopOrderline, creditNoteDocumentUrlList);
    const productinfo = productBasicData(sopOrderline, currencyCode);
    const { sopBundleData, hasBundleSOPCancelledItem } = createSOPBundle(sopOrderline, creditNoteDocumentUrlList);
    data.push({
      ...productinfo,
      quantity: sopOrderline?.bundleProducts?.length
        ? Number(sopOrderline?.bundleQuantity)
        : Number(sopOrderline?.orderedQuantity),
      price: sopOrderline?.bundleProducts?.length
        ? Number(sopOrderline?.calculatedBundleOriginalLineTotalValue)
        : Number(sopOrderline?.originalLineTotalValue),
      pointValue: sopOrderline?.bundleProducts?.length
        ? sopOrderline?.calculatedBundleOriginalLinePVAmount?.toString()
        : sopOrderline?.calculatedSubscriptionOriginalLinePVAmount?.toString(),
      businessVolume: calculateBvValue(sopOrderline),
      orderLineId: sopOrderline?.orderLineId,
      showZeroAsFree: !isReplacementOrder,
      inventoryReservationLineId: sopOrderline?.inventoryReservationLineId,
      strikeThroughPrice: 0,
      bundle: sopBundleData,
      derivedAvailablityStatus: checkDerivedAvailablityStatus(sopOrderline),
      firstAvailableFutureDate: dateTimeFormatter(
        sopOrderline?.estimatedShipDate,
        DateFormats.MEDIUM_DATE,
        undefined,
        DateOrTime.DATE_ONLY,
      ),
    } as SubscriptionProductType);
    if (
      (sopOrderline?.cancelledQuantity && sopOrderline?.cancelledQuantity > 0 && !hasSOPCancelledItems) ||
      hasBundleSOPCancelledItem
    ) {
      hasSOPCancelledItems = true;
    }
  });
  return { data, hasSOPCancelledItems };
}

export function getSubscriptionProductMapping(
  sopMainProduct: OrderLine,
  currencyCode?: string,
  isReplacementOrder?: boolean,
  creditNoteDocumentUrlList?: Array<CreditNoteDocumentList>,
) {
  const { data, hasSOPCancelledItems } = createSOPProductData(
    sopMainProduct,
    currencyCode,
    isReplacementOrder,
    creditNoteDocumentUrlList,
  );
  const sopData: ISubscriptionProduct = {
    productName: sopMainProduct?.subscriptionPlanName || '',
    productPrice: Number(sopMainProduct?.calculatedSubscriptionOriginalLineTotalValue),
    pointValue: sopMainProduct?.calculatedSubscriptionOriginalLinePVAmount?.toString(),
    businessVolume: sopMainProduct?.calculatedSubscriptionOriginalLineBVAmount?.toString(),
    minOrder: Number(sopMainProduct?.subscriptionElementSequence),
    maxOrder: Number(sopMainProduct?.subscriptionTotalShipments),
    subscriptionId: sopMainProduct?.subscriptionCode,
    link: sopMainProduct?.media?.main?.publicUrl || '',
    alt: sopMainProduct?.media?.main?.altText || '',
    currency: currencyCode || '',
    productId: sopMainProduct.baseItemId,
    showZeroAsFree: !isReplacementOrder,
    subscriptionProducts: data,
    isSubscriptionCompensated: sopMainProduct.isSubscriptionCompensated,
  };
  return { sopData, hasSOPCancelledItems };
}

function getReOrderedProduct(id: string, reOrderProducts: CartEntryDetails[]) {
  const product = reOrderProducts.find((obj) => obj.product.id === id);
  const index = reOrderProducts.findIndex((obj) => obj.product.id === id);
  reOrderProducts.splice(index, 1);
  return product;
}

function getProductQuantity(product: ProductData, bundleIds: string[], bundleObjArr: CartEntryDetails[]) {
  let quantity = 0;
  const arrIndex = bundleIds.indexOf(product?.items[0]?.itemNumber || '');
  if (arrIndex !== -1) {
    // eslint-disable-next-line
    quantity = bundleObjArr[arrIndex].quantity;
    bundleIds.splice(arrIndex, 1);
    bundleObjArr.splice(arrIndex, 1);
  }
  return quantity;
}

function getProductVariantId(product: ProductData, bundleIds: string[], bundleObjArr: CartEntryDetails[]) {
  const selectedVariant = product.variantGroup?.optionMap?.find((option) => {
    const currentVariantItemId = option?.product?.items?.length ? option.product.items[0].itemNumber : '';
    const hasVariant = currentVariantItemId && bundleIds.includes(currentVariantItemId || '');
    const arrIndex = bundleIds.indexOf(currentVariantItemId || '');
    if (hasVariant && arrIndex !== -1) {
      bundleIds.splice(arrIndex, 1);
      bundleObjArr.splice(arrIndex, 1);
    }
    return hasVariant;
  });

  const variantItemId = selectedVariant?.product?.items?.length
    ? selectedVariant.product.items[0].itemNumber
    : undefined;
  return variantItemId || product?.id;
}

function getSlotEntries(slot: Slot, bundleIds: Array<string>, bundleObjArr: CartEntryDetails[], quantity: number) {
  const entries: SlotEntry[] = [];
  let maxQuantity = 0;
  slot?.products?.forEach((product: ProductData) => {
    if (bundleIds?.includes(product.items[0].itemNumber || '')) {
      if (slot.type === BundleTypesEnums.POOL && maxQuantity < (slot.maxQty || 0)) {
        const productQuantity = getProductQuantity(product, bundleIds, bundleObjArr);
        maxQuantity += Number(productQuantity / quantity);
        entries.push({
          product: {
            id: product?.items?.length && product?.items[0].itemNumber ? product.items[0].itemNumber : '',
          },
          quantity: (productQuantity !== 0 ? Number(productQuantity / quantity) : slot.minQty) || 0,
        });
      }
      if (slot.type !== BundleTypesEnums.POOL) {
        entries.push({
          product: {
            id:
              slot.type === BundleTypesEnums.VARIANT
                ? getProductVariantId(product, bundleIds, bundleObjArr)
                : product?.items?.length && product?.items[0].itemNumber
                ? product.items[0].itemNumber
                : '',
          },
          quantity: slot.minQty || 0,
        });
      }
    } else if (slot.type === BundleTypesEnums.VARIANT) {
      const selectedVariantId = getProductVariantId(product, bundleIds, bundleObjArr);
      if (selectedVariantId !== product.id) {
        entries.push({
          product: {
            id: selectedVariantId,
          },
          quantity: slot.minQty || 0,
        });
      }
    }
  });
  return entries;
}

export function prepareSlotsData(bundleRes: Products, reOrderProducts: CartEntryDetails[]) {
  const reOrderProductsData: CartEntryDetails[] = [];
  const bundleIds: Array<string> = [];
  const bundleObjArr: CartEntryDetails[] = [];
  reOrderProducts?.forEach((reOrderProduct) => {
    reOrderProduct.bundleSlots?.forEach((slot) => {
      slot.entries.forEach((entry) => {
        bundleIds.push(entry.product.id);
        bundleObjArr.push(entry);
      });
    });
  });
  const reOrderProductsClone = [...reOrderProducts];
  bundleRes?.products?.forEach((product) => {
    const reOrderProduct = getReOrderedProduct(product.id, reOrderProductsClone);
    const obj: CartEntryDetails = {
      product: { id: product.id },
      quantity: reOrderProduct?.quantity || 0,
    };
    if (product.bundle?.slots) {
      const bundleData: BundleSlot[] = [];
      product.bundle?.slots.forEach((slot, index) => {
        if (slot.products) {
          bundleData.push({
            id: `${index}`,
            entries: getSlotEntries(slot, bundleIds, bundleObjArr, reOrderProduct?.quantity || 0),
          });
        }
      });
      obj['bundleSlots'] = bundleData;
    }
    reOrderProductsData.push(obj);
  });
  reOrderProducts.forEach((obj) => {
    if (!obj.bundleSlots) {
      reOrderProductsData.push(obj);
    }
  });
  return reOrderProductsData;
}

export function getGCOData(
  order: Entity,
  productInfo: Array<IProductWithImageInterface>,
  hasCancelledItems: boolean,
  cancelCodesCMS?: Array<ICancelCodeCMS>,
) {
  return {
    orderId: order?.salesOrderId || '',
    name:
      order?.orderForSalesAccount?.accountId === order?.orderingSalesAccount?.accountId
        ? `${order?.orderForSalesAccount?.name} (Me)`
        : `${order?.orderForSalesAccount?.name || ''} ${
            order?.orderForSalesAccount?.accountId ? `(${order?.orderForSalesAccount?.accountId})` : ''
          }`,
    products: productInfo,
    totalAmount: `${formatPrice(order?.totalOrderValue || 0, order?.isoCurrencyCode)}`,
    totalAmPoints: order?.totalMemberPoints?.toString() || '',
    totalPVAmount: order?.totalPVAmount?.toString() || '',
    totalBVAmount: order?.totalBVAmount?.toString() || '',
    isCancelledOrder: isOrderCancelled(order?.calculatedOrderStatus || ''),
    hasCancelledItems: hasCancelledItems,
    hasTNAItems: !hasCancelledItems && order?.calculatedHasBackOrderLines,
    cancellationDetails: getOrderCancellationDetails(order.orderLines, cancelCodesCMS)?.[0],
    calculatedOrderStatus: order?.calculatedOrderStatus || '',
    orderForAboName: order?.orderForSalesAccount?.name,
    orderForAboId: order?.orderForSalesAccount?.accountId,
    originalOrderStatus: order?.orderStatus,
  };
}

export function getShippingDetails(
  order: Entity = {},
  orderType = '',
  deliveryInstructions = '',
  orderDetails: IOrderDetails,
) {
  const orderLines = order?.orderLines;
  let addressReference = '';
  if (orderLines?.length) {
    addressReference +=
      orderType === OrderDeliveryTypeEnum.HOMEDELIVERY
        ? order?.calculatedShipAddressReference?.[0]
        : order?.calculatedPickUpAddressReference?.[0];
  }

  const data = getAddressObj(
    order?.addresses || [],
    addressReference,
    orderType,
    deliveryInstructions,
    order?.saleLocation || '',
    order?.orderEmail,
  );
  const orderDeliveryType = getOrderDeliveryType(orderType);
  switch (orderDeliveryType) {
    case OrderDeliveryTypeEnum.HOMEDELIVERY:
      orderDetails.shippingDetails = data;
      orderDetails.deliveryInstructions = deliveryInstructions.trim();
      break;
    case OrderDeliveryTypeEnum.BOPIS:
      orderDetails.pickupDetails = data;
      orderDetails.deliveryInstructions = deliveryInstructions;
      orderDetails.pickupDetails.pickUpPhone = order?.orderPhoneNumber;
      break;

    default:
      orderDetails.shippingDetails = {};
  }
}

export function formatYearMonth(yyyymm?: string): string {
  try {
    if (!yyyymm || !/^\d{6}$/.test(yyyymm)) {
      return yyyymm ?? '';
    }

    const year = parseInt(yyyymm.slice(0, 4), 10);
    const month = parseInt(yyyymm.slice(4, 6), 10);

    if (month < 1 || month > 12) {
      return yyyymm;
    }

    const localeFromConfig = String(ConfigService?.getConfigs?.()?.NEXT_PUBLIC_LOCALE ?? '');
    const supportedLocales = Intl.DateTimeFormat.supportedLocalesOf(localeFromConfig);
    const safeLocale = supportedLocales.length > 0 ? supportedLocales[0] : 'en-US';

    const date = new Date(year, month - 1);
    return new Intl.DateTimeFormat(safeLocale, { year: 'numeric', month: 'long' }).format(date);
  } catch (error) {
    ConsoleLogger.error('formatYearMonth failed:', error);
    return yyyymm ?? '';
  }
}
